import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNummernTreffer } from "@/lib/nummernsuche";
import { Package, FileText, ClipboardCheck, Receipt, Download, Star } from "lucide-react";
import { toast } from "sonner";
import { Button, Badge, Card } from "@/components/common";
import { api } from "@/lib/api";
import { QuotesPage } from "@/pages/QuotesPage";
import { OrdersPage } from "@/pages/OrdersPage";
import { InvoicesPage } from "@/pages/InvoicesPage";
import { DocumentTemplatesPanel } from "@/components/DocumentTemplatesPanel";
import { useF1Help } from "@/lib/useF1Help";

const TABS = [
  { id: "quotes", label: "Angebote", icon: FileText },
  { id: "orders", label: "Aufträge", icon: ClipboardCheck },
  { id: "invoices", label: "Rechnungen", icon: Receipt },
  { id: "templates", label: "Vorlagen", icon: Star },
];

// Nummernsuche: Reiter je Dokumentart
const ART_TAB = { angebot: "quotes", auftrag: "orders", rechnung: "invoices" };

const DokumenteModulPage = () => {
  useF1Help("hilfe_dokumente");
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("quotes");
  const [stats, setStats] = useState(null);
  // 07.10.2026: ein Suchbegriff für alle Reiter; eine Dokumentnummer wird über alle Arten gesucht
  const [suche, setSuche] = useState("");
  const treffer = useNummernTreffer(suche);

  // Treffer nur in einem anderen Reiter -> automatisch dorthin wechseln
  useEffect(() => {
    if (!treffer.length) return;
    const tabs = Array.from(new Set(treffer.map((t) => ART_TAB[t.art])));
    if (!tabs.includes(activeTab) && tabs.length === 1) setActiveTab(tabs[0]);
  }, [treffer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/modules/dokumente/stats").then(res => setStats(res.data)).catch(() => {});
  }, [activeTab]);

  const handleExport = async () => {
    try {
      const res = await api.get("/modules/dokumente/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dokumente_modul_${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Alle Dokumente exportiert");
    } catch { toast.error("Fehler beim Export"); }
  };

  return (
    <div data-testid="dokumente-modul-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4 lg:mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            <h1 className="text-2xl lg:text-4xl font-bold">Dokumente</h1>
            <Badge variant="default" className="text-xs">Solo</Badge>
          </div>
          {stats && (
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span>{stats.quotes} Angebote</span>
              <span>{stats.orders} Aufträge</span>
              <span>{stats.invoices} Rechnungen</span>
              {stats.total_invoiced > 0 && (
                <span className="font-medium text-foreground">
                  Gesamt: {stats.total_invoiced.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                </span>
              )}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="btn-export-dokumente">
          <Download className="w-4 h-4" /> Export
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 border-b">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const count = stats ? stats[tab.id] || 0 : "";
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`dokumente-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {count !== "" && <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Nummernsuche: Treffer über alle Dokumentarten */}
      {treffer.length > 0 && activeTab !== "templates" && (
        <Card className="p-3 mb-4 border-blue-200 bg-blue-50/50" data-testid="nummern-treffer">
          <p className="text-xs font-semibold text-blue-900 mb-1">Gefunden ({treffer.length})</p>
          {treffer.map((t) => (
            <div key={`${t.art}-${t.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm py-0.5" data-testid={`nummern-treffer-${t.id}`}>
              <Badge variant="default" className="text-xs">{t.art_label}</Badge>
              <span className="font-mono font-medium">{t.nummer}</span>
              <span className="text-muted-foreground">{t.kunde_in_kartei || t.kunde_im_dokument}</span>
              {t.kunde_abweichend && <span className="text-xs text-amber-800 bg-amber-100 px-1.5 rounded-sm">Achtung: im Dokument steht „{(t.kunde_im_dokument || "").split("\n")[0]}“</span>}
              {ART_TAB[t.art] !== activeTab && (
                <button className="text-xs text-primary hover:underline" onClick={() => setActiveTab(ART_TAB[t.art])}>zum Reiter {t.art_label === "Auftrag" ? "Aufträge" : t.art_label === "Angebot" ? "Angebote" : "Rechnungen"}</button>
              )}
              <button className="text-xs text-primary hover:underline" onClick={() => navigate(t.edit_url)}>öffnen</button>
            </div>
          ))}
        </Card>
      )}

      {/* Tab Content - Bestehende Seiten */}
      <div>
        {activeTab === "quotes" && <QuotesPage suche={suche} setSuche={setSuche} />}
        {activeTab === "orders" && <OrdersPage suche={suche} setSuche={setSuche} />}
        {activeTab === "invoices" && <InvoicesPage suche={suche} setSuche={setSuche} />}
        {activeTab === "templates" && <DocumentTemplatesPanel variant="embedded" />}
      </div>
    </div>
  );
};

export { DokumenteModulPage };
